import { Component, OnInit, Output, EventEmitter } from '@angular/core';
import { ConsultarCiudadesService } from '../../../../../../services/cotizador-contratante/consultar-ciudades.service';
import { ConsultarCoberturasService } from '../../../../../../services/cotizador-contratante/consultar-coberturas.service';
import { NetworkingBeneficiary } from '../../../../../../services/beneficiario/beneficiario.service';
import { CiudadCem } from '../../../../../../models/cotizador-contratante/consultar-ciudades/consultar-ciudades-response.model';
import { TipoViaItem } from '../../../../../../models/cotizador-contratante/consultar-tipo-via/response/consultar-tipo-via-response.model';
import { ConsultarCoberturasRequest } from '../../../../../../models/cotizador-contratante/consultar-coberturas/request/consultar-coberturas-request.model';
import { NgxSpinnerService } from 'ngx-spinner';
import { MatDialog } from '@angular/material/dialog';
import { PopUpMensajeComponent, PopUpMensajeData } from '../pop-up-mensaje/pop-up-mensaje.component';

@Component({
  selector: 'app-validacion-area-cobertura',
  templateUrl: './validacion-area-cobertura.component.html',
  styleUrls: ['./validacion-area-cobertura.component.css']
})
export class ValidacionAreaCoberturaComponent implements OnInit {
  // Propiedades para el formulario de dirección
  tipoVia: string = '';
  numeroVia: string = '';
  numeroPlaca1: string = '';
  numeroPlaca2: string = '';
  municipio: string = '';
  mostrarAlerta: boolean = false;
  cemCoberturaUrl: string = 'https://oficinavirtualmp.coomeva.com.co/AdministradorContenido/#/visor';
  
  // Lista de ciudades disponibles
  ciudades: CiudadCem[] = [];
  ciudadesCargadas: boolean = false;

  // Lista de tipos de vía desde el servicio (validación CEM)
  tipoVias: TipoViaItem[] = [];

  /** Emite cuando la cobertura es inválida para que el padre deseleccione el programa CEM */
  @Output() coberturaInvalida = new EventEmitter<void>();

  /**
   * Estado de cobertura: true solo cuando el usuario ha hecho "Validar cobertura" y la respuesta fue aprobada.
   */
  coberturaValida: boolean = false;

  /** Emite cuando cambia el estado (true = aprobada, false = no validada o inválida) */
  @Output() coberturaValidaChange = new EventEmitter<boolean>();

  constructor(
    private consultarCiudadesService: ConsultarCiudadesService,
    private consultarCoberturasService: ConsultarCoberturasService,
    private networking: NetworkingBeneficiary,
    private spinner: NgxSpinnerService,
    private dialog: MatDialog
  ) { }

  ngOnInit(): void {
    this.cargarCiudades();
    this.cargarTiposVia();
  }

  /**
   * Carga los tipos de vía desde el servicio (misma lógica que dialog-beneficiary para validación CEM).
   */
  cargarTiposVia(): void {
    this.networking.consultarTiposVia().subscribe({
      next: (lista: TipoViaItem[]) => {
        this.tipoVias = lista || [];
      },
      error: () => {
        this.tipoVias = [];
      }
    });
  }

  /**
   * Carga las ciudades desde el servicio
   */
  cargarCiudades(): void {
    this.spinner.show();
    this.consultarCiudadesService.consultarCiudades().subscribe({
      next: (response) => {
        this.spinner.hide();
        if (response.status && response.data?.jsonRespuesta) {
          this.ciudades = response.data.jsonRespuesta;
          this.ciudadesCargadas = true;
          console.log('Ciudades cargadas:', this.ciudades);
        } else {
          console.error('Error: La respuesta no contiene ciudades válidas');
          this.ciudades = [];
        }
      },
      error: (error) => {
        this.spinner.hide();
        console.error('Error al cargar ciudades:', error);
        this.ciudades = [];
      }
    });
  }
  
  onKeyDownAlfanumerico(event: KeyboardEvent, allowSpaces = false): void {
    const tecla = event.key;

    // Permitir teclas de control
    const teclasPermitidas = [
      'Backspace',
      'Delete',
      'Tab',
      'Escape',
      'Enter',
      'ArrowLeft',
      'ArrowRight',
      'ArrowUp',
      'ArrowDown',
      'Home',
      'End'
    ];

    if (teclasPermitidas.includes(tecla) || event.ctrlKey || event.metaKey) {
      return;
    }

    const regex = allowSpaces
      ? /^[a-zA-Z0-9 ]$/
      : /^[a-zA-Z0-9]$/;

    if (!regex.test(tecla)) {
      event.preventDefault();
    }
  }

  private limpiarAlfanumerico(value: string, maxLength: number, allowSpaces = false): string {
    const texto = String(value ?? '');
    const regex = allowSpaces ? /[^a-zA-Z0-9 ]/g : /[^a-zA-Z0-9]/g;
    return texto.replace(regex, '').slice(0, maxLength);
  }

  onNumeroViaInput(value: string): void {
    this.numeroVia = this.limpiarAlfanumerico(value, 10, true);
  }

  onNumeroPlaca1Input(value: string): void {
    this.numeroPlaca1 = this.limpiarAlfanumerico(value, 5, false);
  }

  onNumeroPlaca2Input(value: string): void {
    this.numeroPlaca2 = this.limpiarAlfanumerico(value, 5, false);
  }

  onPasteNumeroVia(event: ClipboardEvent): void {
    const pasted = event.clipboardData?.getData('text') ?? '';
    if (!/^[a-zA-Z0-9 ]*$/.test(pasted)) {
      event.preventDefault();
    }
  }

  onPasteNumeroPlaca(event: ClipboardEvent): void {
    const pasted = event.clipboardData?.getData('text') ?? '';
    if (!/^[a-zA-Z0-9]*$/.test(pasted)) {
      event.preventDefault();
    }
  }

  /**
   * Obtiene el nombre de la ciudad a partir del código seleccionado
   */
  obtenerNombreCiudad(): string {
    if (!this.municipio) {
      return '';
    }
    const ciudad = this.ciudades.find(c => c.codigo === this.municipio);
    return ciudad ? ciudad.municipio : '';
  }

  /**
   * Método para concatenar la dirección en formato "CALLE 10 A # 72-36"
   * @returns La dirección concatenada
   */
  concatenarDireccion(): string {
    const partes: string[] = [];
    
    // Tipo de vía (CALLE o CARRERA)
    if (this.tipoVia) {
      partes.push(this.tipoVia);
    }
    
    // Número de vía
    if (this.numeroVia) {
      partes.push(this.numeroVia);
    }
    
    // Número de placa con formato # 72-36
    if (this.numeroPlaca1 || this.numeroPlaca2) {
      let placa = '#';
      if (this.numeroPlaca1) {
        placa += ` ${this.numeroPlaca1}`;
      }
      if (this.numeroPlaca2) {
        placa += `-${this.numeroPlaca2}`;
      }
      partes.push(placa);
    }
    
    // Resultado: "CALLE 10 A # 72-36" (sin complemento)
    return partes.join(' ').trim();
  }

  /**
   * Método para validar cobertura
   */
  validarCobertura(): void {
    // Validar que los campos requeridos estén llenos
    if (!this.tipoVia || !this.numeroVia || !this.numeroPlaca1 || !this.municipio) {
      this.mostrarMensaje('Error', 'Por favor complete todos los campos requeridos', 'error');
      return;
    }

    const direccion = this.concatenarDireccion();
    const nombreCiudad = this.obtenerNombreCiudad();

    if (!direccion || !nombreCiudad) {
      this.mostrarMensaje('Error', 'No se pudo construir la dirección o ciudad', 'error');
      return;
    }

    // Construir el request según la estructura requerida
    const request: ConsultarCoberturasRequest = {
      nombreClave: 'validarCoberturaWs',
      queryParams: null,
      pathVariables: null,
      body: {
        direccion: direccion,
        ciudad: nombreCiudad
      }
    };

    console.log('Request de validación:', request);

    this.spinner.show();
    this.consultarCoberturasService.validarCobertura(request).subscribe({
      next: (response) => {
        this.spinner.hide();
        console.log('Respuesta de validación:', response);
        
        if (response.status && response.data) {
          if (response.data.aprobado === 'S') {
            // Cobertura válida - mostrar mensaje de éxito y exponer estado
            this.mostrarAlerta = false;
            this.coberturaValida = true;
            this.coberturaValidaChange.emit(true);
            this.mostrarMensaje(
              'Validación Exitosa',
              'Cobertura disponible en la dirección especificada.',
              'success'
            );
          } else if (response.data.aprobado === 'N') {
            // Cobertura no válida - mostrar alerta, pop-up con link al mapa y deseleccionar programa
            this.mostrarAlerta = true;
            this.coberturaValida = false;
            this.coberturaValidaChange.emit(false);
            const textoConLink = `¡Este programa no tiene cobertura en la dirección especificada!.`;
            //const textoConLink = `¡Este programa no tiene cobertura en la dirección especificada! Para ver el mapa de coberturas haga <a href="${this.cemCoberturaUrl}" target="_blank" rel="noopener noreferrer">clic aquí</a>.`;
            this.mostrarMensaje('Cobertura No Válida', textoConLink, 'error');
            
            /* Se comenta la emisión para que el componente  mantenga seleccionado el carnet CEM y no borre la dirección */
            //this.coberturaInvalida.emit();
          }
        }
      },
      error: (error) => {
        this.spinner.hide();
        console.error('Error al validar cobertura:', error);
        this.mostrarAlerta = true;
        this.coberturaValida = false;
        this.coberturaValidaChange.emit(false);
        this.mostrarMensaje(
          'Error',
          'Ocurrió un error al validar la cobertura. Por favor intente nuevamente.',
          'error'
        );
        
        /* Se comenta la emisión para que el componente  mantenga seleccionado el carnet CEM y no borre la dirección */
        //this.coberturaInvalida.emit();
      }
    });
  }

  /**
   * Muestra un mensaje usando el componente PopUpMensaje
   */
  private mostrarMensaje(titulo: string, texto: string, tipo: 'success' | 'error' | 'warning' | 'info'): void {
    this.dialog.open(PopUpMensajeComponent, {
      width: '500px',
      maxWidth: '90vw',
      disableClose: false,
      data: {
        titulo: titulo,
        texto: texto,
        tipoIcono: tipo,
        textoBoton: 'ACEPTAR'
      } as PopUpMensajeData
    });
  }
}
